/**
 * Shared Users settings (`@darwin-launchpad/legacy-shared/app-settings`) for RedactIt, behind the
 * build-time staging flag `VITE_ENABLE_USERS_SETTINGS`.
 *
 * The page talks to the shared launchpad backend (`VITE_LAUNCHPAD_API_BASE_URL`),
 * not to RedactIt's own API, with the Darwin Identity bearer the app already
 * holds. Env values are read through functions so tests can `vi.stubEnv` them.
 */
import { createMockSettingsApi, createSettingsApi } from '@darwin-launchpad/legacy-shared/app-settings';
import { useAuth } from '../contexts/IframeAuthContext';
import { getAuthTokensService } from './auth/AuthTokensService';

type SettingsApi = ReturnType<typeof createSettingsApi>;

/** RedactIt's slug in the Systems Registry (its API prefix is `launchpad/redactit/api`). */
export const USERS_SETTINGS_APP_ID = 'redactit';

/** Identity roles the shared backend lets manage members: `darwin` or the app's `*_admin`. */
export const USERS_SETTINGS_ADMIN_ROLES = ['darwin', 'redactit_admin'] as const;

export const USERS_SETTINGS_PATH = '/settings/users';

function readEnv(name: string): string | undefined {
  const value = (import.meta.env as Record<string, string | boolean | undefined>)[name];
  if (value === undefined || value === null) return undefined;
  const trimmed = String(value).trim();
  return trimmed === '' ? undefined : trimmed;
}

/** Off unless the build sets the flag to exactly `true`. */
export function usersSettingsEnabled(): boolean {
  return readEnv('VITE_ENABLE_USERS_SETTINGS') === 'true';
}

export function launchpadApiBaseUrl(): string | undefined {
  return readEnv('VITE_LAUNCHPAD_API_BASE_URL')?.replace(/\/+$/, '');
}

/** The dev auth bypass has no real bearer, so it gets the in-memory demo API. */
function mockApiEnabled(): boolean {
  return !import.meta.env.PROD && readEnv('VITE_DEV_BYPASS_AUTH') === 'true';
}

export function canManageUsers(roles: readonly string[] | null | undefined): boolean {
  return !!roles && USERS_SETTINGS_ADMIN_ROLES.some((role) => roles.includes(role));
}

/**
 * True when the Users settings entry may be shown: the staging flag is on and
 * the caller has `darwin` or `redactit_admin` (from `/auth/me` when View-as is active).
 */
export function useCanManageUsersSettings(): boolean {
  const { user, rolesFromToken } = useAuth();
  const roles = user?.roles ?? user?.current_tenant?.roles ?? rolesFromToken;
  return usersSettingsEnabled() && canManageUsers(roles);
}

let cached: { key: string; api: SettingsApi | null } | undefined;

/**
 * The page's API client, created once per configuration (a new client reloads
 * the page). `null` when the backend base URL is not configured.
 */
export function getUsersSettingsApi(): SettingsApi | null {
  const mock = mockApiEnabled();
  const baseUrl = launchpadApiBaseUrl();
  const key = mock ? 'mock' : (baseUrl ?? '');
  if (cached?.key === key) return cached.api;
  let api: SettingsApi | null = null;
  if (mock) {
    api = createMockSettingsApi(USERS_SETTINGS_APP_ID);
  } else if (baseUrl) {
    api = createSettingsApi({
      apiBaseUrl: baseUrl,
      appId: USERS_SETTINGS_APP_ID,
      // Asked on every request, so a token refreshed by the hidden iframe is picked up.
      getAccessToken: () => getAuthTokensService().getCurrentCredentials()?.access_token?.token,
    });
  }
  cached = { key, api };
  return api;
}
